import { useEffect, useMemo, useState, type FormEvent } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { PLAN_CATALOG, isPlanId, type PlanId } from "@/lib/plan-catalog";

const API_BASE_URL = import.meta.env.VITE_API_BASE_URL as string | undefined;

type SignupStep = "plan" | "details";

interface SignupPlan {
  id: PlanId;
  name: string;
  price: string;
  summary: string;
  endpoints: string;
  retention: string;
  trial: string;
  popular?: boolean;
}

interface SignupPayload {
  company_name: string;
  admin_name: string;
  admin_email: string;
  selected_plan: PlanId;
}

interface SignupSuccess {
  email: string;
  requiresVerification: boolean;
}

// Page-local marketing copy; the plan facts (price, endpoints, retention) come
// from the shared catalog.
const SIGNUP_COPY: Record<PlanId, { summary: string; trial: string }> = {
  starter: { summary: "A 30-day trial to kick the tires.", trial: "30-day trial" },
  essential: { summary: "Lean teams getting started.", trial: "30-day trial" },
  growth: { summary: "Most SMBs land here.", trial: "30-day trial" },
  business: { summary: "Dedicated security teams.", trial: "30-day trial" },
  enterprise: { summary: "MSPs and larger organizations.", trial: "Sales-led trial" },
};

const SIGNUP_PLANS: readonly SignupPlan[] = PLAN_CATALOG.filter((plan) => !plan.hidden).map((plan) => ({
  id: plan.id,
  name: plan.name,
  price: `${plan.price}${plan.per}`,
  summary: SIGNUP_COPY[plan.id].summary,
  endpoints: plan.endpointsCap ? `${plan.endpointsCap} endpoints` : "Unlimited endpoints",
  retention: plan.retention,
  trial: SIGNUP_COPY[plan.id].trial,
  popular: plan.popular,
}));

export default function SignupPage() {
  const [searchParams] = useSearchParams();
  const requestedPlan = searchParams.get("plan");
  const initialPlan: PlanId =
    isPlanId(requestedPlan) && SIGNUP_PLANS.some((plan) => plan.id === requestedPlan)
      ? requestedPlan
      : "starter";

  const [step, setStep] = useState<SignupStep>("plan");
  const [selectedPlan, setSelectedPlan] = useState<PlanId>(initialPlan);
  const [companyName, setCompanyName] = useState("");
  const [adminName, setAdminName] = useState("");
  const [adminEmail, setAdminEmail] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<SignupSuccess | null>(null);
  const [mounted, setMounted] = useState(false);

  useEffect(() => {
    setMounted(true);
  }, []);

  const selectedPlanDetails = useMemo(
    () => SIGNUP_PLANS.find((plan) => plan.id === selectedPlan) ?? SIGNUP_PLANS[0],
    [selectedPlan],
  );

  const canSubmit = Boolean(companyName.trim() && adminName.trim() && adminEmail.trim());

  async function handleSubmit(e: FormEvent) {
    e.preventDefault();
    if (!canSubmit) {
      setError("Please fill in your company, name and email.");
      return;
    }

    setError(null);
    setIsSubmitting(true);

    const payload: SignupPayload = {
      company_name: companyName.trim(),
      admin_name: adminName.trim(),
      admin_email: adminEmail.trim(),
      selected_plan: selectedPlan,
    };

    const url = API_BASE_URL ? `${API_BASE_URL}/public/signup` : "/public/signup";

    try {
      const res = await fetch(url, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(payload),
      });

      if (!res.ok) {
        let message = `Request failed (${res.status})`;
        try {
          const body = await res.json();
          if (body?.messages) message = body.messages.join(". ");
          else if (body?.message) message = body.message;
          else if (body?.detail) message = body.detail;
        } catch {
          // ignore JSON parse errors
        }
        setError(message);
        return;
      }

      const data = (await res.json().catch(() => ({}))) as { requires_verification?: boolean };
      setSuccess({ email: payload.admin_email, requiresVerification: Boolean(data.requires_verification) });
    } catch {
      setError("Network error — please check your connection and try again.");
    } finally {
      setIsSubmitting(false);
    }
  }

  if (success) {
    return (
      <div className="relative flex min-h-screen items-center justify-center overflow-hidden bg-background px-6">
        <GridBg />
        <div className="pointer-events-none absolute left-1/2 top-1/2 h-[600px] w-[600px] -translate-x-1/2 -translate-y-1/2 rounded-full bg-primary/[0.06] blur-[120px]" />

        <div className={`relative z-10 w-full max-w-[440px] transition-all duration-700 ease-out ${mounted ? "translate-y-0 opacity-100" : "translate-y-4 opacity-0"}`}>
          <div className="rounded-2xl bg-gradient-to-b from-primary/20 via-primary/5 to-transparent p-px">
            <div className="rounded-2xl bg-card/90 px-8 pb-8 pt-10 backdrop-blur-xl">
              <div className="mb-6 flex justify-center">
                <div className="relative">
                  <div className="absolute -inset-3 rounded-full bg-primary/10 blur-md" />
                  <div className="relative flex h-16 w-16 items-center justify-center rounded-full border border-primary/20 bg-primary/[0.08]">
                    <MailIcon className="h-8 w-8 text-primary" />
                  </div>
                </div>
              </div>

              <div className="mb-6 text-center">
                <h1 className="mb-1.5 text-[22px] font-semibold tracking-tight text-white">
                  {success.requiresVerification ? "Check your email" : "Registration received"}
                </h1>
                <p className="text-[13px] text-slate-400">
                  {success.requiresVerification ? "We sent your verification link to" : "We'll contact you at"}
                </p>
              </div>

              <div className="mb-6 rounded-lg border border-primary/10 bg-primary/[0.04] px-4 py-3 text-center">
                <p className="text-[14px] font-medium text-primary">{success.email}</p>
              </div>

              <p className="mb-6 text-center text-[13px] leading-relaxed text-slate-400">
                {success.requiresVerification
                  ? `Your ${selectedPlanDetails.name} account is reserved. Verify your email to activate the 30-day trial.`
                  : `We'll be in touch to arrange activation of your ${selectedPlanDetails.name} account. Your 30-day trial starts when we activate your access; nothing to pay until then.`}
              </p>

              <Link to={success.requiresVerification ? "/login" : "/"}>
                <Button
                  size="lg"
                  className="w-full rounded-xl border-0 bg-primary py-6 text-[14px] font-medium tracking-wide text-white shadow-lg shadow-primary/20 transition-all duration-300 hover:bg-primary hover:shadow-primary/30"
                >
                  {success.requiresVerification ? "Go to Sign In" : "Back to home"}
                </Button>
              </Link>
              {!success.requiresVerification && (
                <p className="mt-4 text-center text-[13px] text-slate-400">
                  Already a customer?{" "}
                  <Link to="/login" className="font-medium text-primary transition-colors hover:text-primary/80">
                    Sign in
                  </Link>
                </p>
              )}
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="relative min-h-screen overflow-hidden bg-background px-6 py-10">
      <GridBg />
      <div className="pointer-events-none absolute left-1/3 top-20 h-[520px] w-[520px] rounded-full bg-primary/[0.07] blur-[120px]" />
      <div className="pointer-events-none absolute bottom-0 right-0 h-[420px] w-[420px] rounded-full bg-emerald-500/[0.05] blur-[120px]" />

      <div className={`relative z-10 mx-auto w-full max-w-6xl transition-all duration-700 ease-out ${mounted ? "translate-y-0 opacity-100" : "translate-y-4 opacity-0"}`}>
        <div className="mb-8 flex flex-wrap items-center justify-between gap-4">
          <Link to="/" className="flex items-center gap-3 text-white no-underline">
            <img src="/logo.png" alt="prayingmantis.cloud" className="h-9 w-auto" />
            <span className="text-sm font-medium tracking-wide text-slate-300">prayingmantis.cloud</span>
          </Link>
          <p className="text-sm text-slate-400">
            Already have an account?{" "}
            <Link to="/login" className="font-medium text-primary transition-colors hover:text-primary/80">
              Sign in
            </Link>
          </p>
        </div>

        <div className="grid gap-6 lg:grid-cols-[0.82fr_1.18fr]">
          <aside className="rounded-3xl border border-white/10 bg-card/70 p-6 backdrop-blur-xl">
            <p className="mb-3 inline-flex rounded-full border border-white/10 bg-white/[0.04] px-3 py-1 text-xs font-medium text-slate-300">
              Create an account
            </p>
            <h1 className="text-4xl font-semibold tracking-tight text-white md:text-5xl">
              Choose a tier. Register for access.
            </h1>
            <p className="mt-4 text-sm leading-6 text-slate-400">
              Pick the plan, add your details, and we'll be in touch to arrange activation. Your 30-day trial starts when we activate your access; nothing to pay until then.
            </p>

            <div className="mt-8 space-y-3">
              {[
                ["plan", "Choose tier"],
                ["details", "Account details"],
              ].map(([id, label], index) => {
                const active = step === id;
                return (
                  <button
                    key={id}
                    type="button"
                    onClick={() => setStep(id as SignupStep)}
                    className={`flex w-full items-center gap-3 rounded-2xl border px-4 py-3 text-left transition ${
                      active ? "border-primary/40 bg-primary/[0.08] text-white" : "border-white/10 bg-white/[0.03] text-slate-400 hover:border-white/20"
                    }`}
                  >
                    <span className="flex h-7 w-7 shrink-0 items-center justify-center rounded-full bg-white/[0.06] text-xs font-semibold">
                      {index + 1}
                    </span>
                    <span className="text-sm font-medium">{label}</span>
                  </button>
                );
              })}
            </div>

            <div className="mt-8 rounded-2xl border border-primary/20 bg-primary/[0.06] p-4">
              <div className="text-xs uppercase tracking-[0.18em] text-primary">Selected</div>
              <div className="mt-2 flex items-end justify-between gap-4">
                <div>
                  <div className="text-xl font-semibold text-white">{selectedPlanDetails.name}</div>
                  <div className="mt-1 text-sm text-slate-400">{selectedPlanDetails.endpoints} · {selectedPlanDetails.retention}</div>
                </div>
                <div className="text-right text-lg font-semibold text-primary">{selectedPlanDetails.price}</div>
              </div>
            </div>
          </aside>

          <main className="rounded-3xl border border-white/10 bg-card/85 p-6 shadow-2xl shadow-black/20 backdrop-blur-xl md:p-8">
            <form onSubmit={handleSubmit}>
              {step === "plan" && (
                <section>
                  <div className="mb-6">
                    <h2 className="text-2xl font-semibold tracking-tight text-white">Choose your tier</h2>
                    <p className="mt-2 text-sm text-slate-400">Every tier includes a 30-day trial once we activate your access.</p>
                  </div>
                  <div className="grid gap-3 md:grid-cols-2 xl:grid-cols-3">
                    {SIGNUP_PLANS.map((plan) => {
                      const active = selectedPlan === plan.id;
                      return (
                        <button
                          key={plan.id}
                          type="button"
                          onClick={() => setSelectedPlan(plan.id)}
                          className={`relative rounded-2xl border p-4 text-left transition ${
                            active ? "border-primary/60 bg-primary/[0.10] shadow-lg shadow-primary/10" : "border-white/10 bg-white/[0.03] hover:border-white/20"
                          }`}
                        >
                          {plan.popular && (
                            <span className="absolute right-4 top-4 rounded-full bg-primary px-2 py-1 text-[10px] font-bold uppercase tracking-wide text-slate-950">
                              Popular
                            </span>
                          )}
                          <div className="text-lg font-semibold text-white">{plan.name}</div>
                          <div className="mt-2 text-2xl font-semibold tracking-tight text-white">{plan.price}</div>
                          <p className="mt-2 min-h-10 text-sm leading-5 text-slate-400">{plan.summary}</p>
                          <div className="mt-4 space-y-2 text-sm text-slate-300">
                            <PlanLine>{plan.endpoints}</PlanLine>
                            <PlanLine>{plan.retention}</PlanLine>
                            <PlanLine>{plan.trial}</PlanLine>
                          </div>
                        </button>
                      );
                    })}
                  </div>
                  <div className="mt-6 flex justify-end">
                    <Button type="button" size="lg" onClick={() => setStep("details")} className="rounded-xl bg-primary px-8 text-white hover:bg-primary/90">
                      Continue to details
                    </Button>
                  </div>
                </section>
              )}

              {step === "details" && (
                <section>
                  <div className="mb-6">
                    <h2 className="text-2xl font-semibold tracking-tight text-white">Account details</h2>
                    <p className="mt-2 text-sm text-slate-400">We'll use these details to contact you and set up your account.</p>
                  </div>
                  <div className="grid gap-4 md:grid-cols-2">
                    <div className="space-y-1.5 md:col-span-2">
                      <Label htmlFor="company-name" className="text-[12px] font-medium uppercase tracking-wider text-slate-400">
                        Company name
                      </Label>
                      <Input
                        id="company-name"
                        type="text"
                        placeholder="Acme Security LLC"
                        value={companyName}
                        onChange={(e) => setCompanyName(e.target.value)}
                        required
                        disabled={isSubmitting}
                        className="rounded-lg border-slate-700/50 bg-slate-800/30 py-5 text-[14px] text-white placeholder:text-slate-600 focus:border-primary/50 focus:ring-primary/20"
                      />
                    </div>
                    <div className="space-y-1.5">
                      <Label htmlFor="admin-name" className="text-[12px] font-medium uppercase tracking-wider text-slate-400">
                        Admin name
                      </Label>
                      <Input
                        id="admin-name"
                        type="text"
                        placeholder="Jane Smith"
                        value={adminName}
                        onChange={(e) => setAdminName(e.target.value)}
                        required
                        disabled={isSubmitting}
                        className="rounded-lg border-slate-700/50 bg-slate-800/30 py-5 text-[14px] text-white placeholder:text-slate-600 focus:border-primary/50 focus:ring-primary/20"
                      />
                    </div>
                    <div className="space-y-1.5">
                      <Label htmlFor="admin-email" className="text-[12px] font-medium uppercase tracking-wider text-slate-400">
                        Admin email
                      </Label>
                      <Input
                        id="admin-email"
                        type="email"
                        placeholder="jane@example.com"
                        value={adminEmail}
                        onChange={(e) => setAdminEmail(e.target.value)}
                        required
                        disabled={isSubmitting}
                        className="rounded-lg border-slate-700/50 bg-slate-800/30 py-5 text-[14px] text-white placeholder:text-slate-600 focus:border-primary/50 focus:ring-primary/20"
                      />
                    </div>
                  </div>
                  {error && (
                    <div className="mt-5 rounded-lg border border-red-500/20 bg-red-500/[0.06] px-4 py-3">
                      <p className="text-[13px] text-red-400">{error}</p>
                    </div>
                  )}

                  <div className="mt-6 flex flex-wrap justify-between gap-3">
                    <Button type="button" variant="outline" onClick={() => setStep("plan")} className="rounded-xl border-white/10 bg-white/[0.03] text-slate-300 hover:bg-white/[0.06] hover:text-white">
                      Back
                    </Button>
                    <Button
                      type="submit"
                      size="lg"
                      disabled={isSubmitting || !canSubmit}
                      className="rounded-xl bg-primary px-8 text-white shadow-lg shadow-primary/20 hover:bg-primary/90 disabled:opacity-50"
                    >
                      {isSubmitting ? (
                        <span className="flex items-center gap-2.5">
                          <span className="h-4 w-4 animate-spin rounded-full border-2 border-current border-t-transparent" />
                          Submitting...
                        </span>
                      ) : (
                        "Register"
                      )}
                    </Button>
                  </div>
                </section>
              )}
            </form>
          </main>
        </div>
      </div>
    </div>
  );
}

function PlanLine({ children }: { children: string }) {
  return (
    <div className="flex items-center gap-2">
      <CheckIcon className="h-4 w-4 text-primary" />
      <span>{children}</span>
    </div>
  );
}

function GridBg() {
  return (
    <div
      className="pointer-events-none absolute inset-0 opacity-[0.04]"
      style={{
        backgroundImage:
          "linear-gradient(rgba(59,130,246,0.5) 1px, transparent 1px), linear-gradient(90deg, rgba(59,130,246,0.5) 1px, transparent 1px)",
        backgroundSize: "48px 48px",
      }}
    />
  );
}

function CheckIcon({ className }: { className?: string }) {
  return (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.8"
      strokeLinecap="round"
      strokeLinejoin="round"
      className={className}
    >
      <path d="M20 6 9 17l-5-5" />
    </svg>
  );
}

function MailIcon({ className }: { className?: string }) {
  return (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.5"
      strokeLinecap="round"
      strokeLinejoin="round"
      className={className}
    >
      <rect width="20" height="16" x="2" y="4" rx="2" />
      <path d="m22 7-8.97 5.7a1.94 1.94 0 0 1-2.06 0L2 7" />
    </svg>
  );
}
